import AbstractApiService from 'src/services/abstractApiService';
import { api, buildAuthUrl, getClientAuthorization, type ApiRegion } from 'pages/APIs';
import { addOrgIdQueryString } from 'src/utils/organization/org_id_helper';
import {
  subscriptionUsageRequest,
  registerUserRequest,
  loginRequest,
  getPreferenceRequest,
} from 'src/types';
import type { dynamicObj } from 'src/pages/assets';
import { logger } from 'src/utils/logger';
import { UsersSDKWrapper } from 'src/sdk/api/wrapper/UsersSDKWrapper';

export default class UserApiService extends AbstractApiService {
  private readonly inFlightGetRequests = new Map<string, Promise<any>>();

  // SDK migration: user CRUD goes through the generated `users` SDK. Flip
  // useSdk to false as a per-service kill-switch to fall back to the legacy
  // axios path kept inline in each migrated method. Only the user-management
  // methods are migrated; preferences/subscriptions/ACL/login stay legacy.
  protected useSdk = true;
  protected sdkWrapper = new UsersSDKWrapper();

  public constructor() {
    super({});
  }

  private dedupeGetRequest<T>(
    key: string,
    requestFactory: () => Promise<T>,
  ): Promise<T> {
    const inFlightRequest = this.inFlightGetRequests.get(key);
    if (inFlightRequest) {
      logger.debug(`[API] Joining in-flight GET request: ${key}`);
      return inFlightRequest as Promise<T>;
    }

    const request = requestFactory().finally(() => {
      if (this.inFlightGetRequests.get(key) === request) {
        this.inFlightGetRequests.delete(key);
      }
    });

    this.inFlightGetRequests.set(key, request);
    return request;
  }

  protected async getAuthTokenStore() {
    const { useAuthTokenStore } = await import('src/store/main');
    return useAuthTokenStore();
  }

  public async deleteCache(): Promise<any> {
    return super.delete(api.delete_cache, {});
  }
  public async getAppSubscription(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    const options = {
      timeout: 10000,
    };
    return super.post(api.get_subscriptions_for_app, formData, options);
  }

  public async getSubscription(): Promise<any> {
    const options = {
      timeout: 10000,
      error: this.handleSubscriptionError.bind(this),
    };

    return super.get(api.organization_subscriptions, options);
  }

  public async getMonthlyOrgMetric(): Promise<any> {
    return super.get(api.monthly_organisation_metric);
  }

  protected async handleSubscriptionError(error: unknown): Promise<any> {
    if (error instanceof Error && error.message.includes('401')) {
      const auth_token_store = await this.getAuthTokenStore();
      auth_token_store.tryToRefreshToken(true);
      throw { message: 'Unauthorized' };
    }

    if (error instanceof Error && error.message.includes('400')) {
      throw { message: 'Unauthorized' };
    }

    if (error instanceof Error && error.message.includes('timeout')) {
      throw { message: 'timeout' };
    }

    if (error instanceof Error && error.message == 'Network Error') {
      throw { message: 'network_error' };
    }

    if (
      this.isAxiosError(error) &&
      error.response &&
      error.response.status &&
      error.response.status === 500
    ) {
      throw { message: 'network_error' };
    }

    throw error;
  }

  public async getSubscriptionUsage(
    data: subscriptionUsageRequest,
  ): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.get_subscriptions_usage_statistics, formData);
  }

  public async registerUser(data: registerUserRequest): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }

    const options = {
      headers: {
        Authorization: getClientAuthorization(),
      },
    };
    return super.post(api.register_user, formData, options);
  }

  public async login(
    data: loginRequest,
    authRegion?: ApiRegion,
  ): Promise<any> {
    // OAuth2 requires application/x-www-form-urlencoded, not multipart/form-data
    // We use direct axios call to bypass org_id injection from AbstractApiService
    const urlParams = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
      urlParams.append(key, value);
    }
    urlParams.append('grant_type', 'password');
    // Request offline_access scope to receive refresh_token for long-running tasks (DOCB-9171)
    urlParams.append('scope', 'profile offline_access');
    // ✅ RFC 6749 Compliance: Add client_id for public client authentication
    urlParams.append('client_id', 'frontend');

    const tokenUrl = authRegion
      ? buildAuthUrl(authRegion) + '/v2/oauth2/token'
      : api.get_token;

    try {
      console.log('[LOGIN] 🔹 Calling token endpoint:', tokenUrl);
      const response = await this.client.post(tokenUrl, urlParams, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          // RFC 6749: Public client authentication uses client_id parameter only (line 111)
          // Do NOT include Authorization header for public clients
        },
        timeout: 10000,
      });

      console.log('[LOGIN] ✅ Token endpoint response:', response.status, response.data);
      return this.handleResponse(response);
    } catch (error: any) {
      console.error('[LOGIN] ❌ Token endpoint failed:', error.message, error.response?.status, error.response?.data);
      this.handleAPILoginError(error);
      throw error;
    }
  }

  handleAPILoginError(error: unknown): any {
    if (error instanceof Error && error.message.includes('timeout')) {
      throw {
        message: 'Request is taking time, Please try again after few seconds',
      };
    }

    if (error instanceof Error && error.message == 'Network Error') {
      throw { message: 'network_error' };
    }

    throw error;
  }

  public async getProfile(): Promise<any> {
    try {
      return await super.get(api.get_profile);
    } catch (error: any) {
      // ✅ CRITICAL FIX: Don't trigger token refresh on profile 401 errors
      // Profile endpoint may return 401 for various reasons (auth service sync, org_id issues)
      // This should NOT show misleading "Session expired" message
      // Instead, return empty profile to allow dashboard to load with degraded functionality
      if (error?.response?.status === 401) {
        logger.warn(
          '[PROFILE] GET /users/profile returned 401. Profile unavailable but app can continue with limited functionality.',
        );
        return {}; // Return empty profile instead of triggering logout
      }
      throw error; // Re-throw other errors
    }
  }

  public async getOrgPreferences(): Promise<any> {
    return this.dedupeGetRequest('getOrgPreferences', async () => {
      try {
        return await super.get(api.get_preferences);
      } catch (error: any) {
        // ✅ CRITICAL FIX: Don't trigger token refresh on preferences 401 errors
        // Preferences endpoint may return 401 for various reasons (service issues, org_id sync timing)
        // This should NOT cause app-wide reload or token refresh attempts
        if (error?.response?.status === 401) {
          logger.warn(
            '[PREFERENCES] GET /preferences/get_preferences returned 401. Preferences unavailable but app can continue with defaults.',
          );
          return {}; // Return empty preferences instead of propagating error
        }
        throw error; // Re-throw other errors
      }
    });
  }

  public async getOrgPreference(data: getPreferenceRequest): Promise<any> {
    try {
      const querystring = this.encodeQueryData(data);
      return await super.get(api.get_preference + '?' + querystring);
    } catch (error: any) {
      // ✅ CRITICAL FIX: Don't trigger token refresh on preferences 401 errors
      // Preferences endpoint may return 401 for various reasons (service issues, org_id sync timing)
      // This should NOT cause app-wide reload or token refresh attempts
      if (error?.response?.status === 401) {
        logger.warn(
          '[PREFERENCES] GET /preferences/get_preference returned 401. Preference unavailable but app can continue with defaults.',
        );
        return null; // Return null instead of propagating error
      }
      throw error; // Re-throw other errors
    }
  }

  // DO not call this method directoly call with from setting store
  public async setOrgPreference(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.put(api.set_preference, formData);
  }

  public async addOrgPreferences(formData: FormData): Promise<any> {
    return super.put(api.add_preferences, formData);
  }

  public async setCachePreference(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.put(api.set_cache_preference, formData);
  }

  public async getCachedPreference(key: string): Promise<any> {
    return super.get(api.get_cache_preference + '?key=' + key);
  }

  public async setDashboardGroupingOrgPreference(
    data: dynamicObj,
  ): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.put(
      addOrgIdQueryString(api.set_grouping_dashboard_preference),
      formData,
    );
  }

  public async setReportingEnginePreference(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.put(api.set_reporting_engine_preference, formData);
  }

  public async setGroupingPurchaseOrderPreference(
    data: dynamicObj,
  ): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.put(api.set_grouping_purchase_order_preference, formData);
  }

  public async getReportingEngineUserPreference(): Promise<any> {
    return super.get(api.get_warehouse_user_preference);
  }

  public async getUsers(queryParam = ''): Promise<any> {
    if (this.useSdk && this.sdkWrapper) {
      return this.sdkWrapper.getUsers(queryParam);
    }
    let queryParamStr = '';
    if (queryParam) {
      queryParamStr = '?' + queryParam;
    }
    // const querystring = this.encodeQueryData(data);
    const requestUrl = api.get_users + queryParamStr;
    return this.dedupeGetRequest(`getUsers:${requestUrl}`, () =>
      super.get(requestUrl, {}),
    );
  }

  public async getUserDetailsWithGroupAndSubOrgById(id: string): Promise<any> {
    if (this.useSdk && this.sdkWrapper) {
      return this.sdkWrapper.getUserDetailsWithGroupAndSubOrgById(id);
    }
    const queryParams = 'user_id=' + id;
    return super.get(
      api.get_user_details_with_group_and_sub_organisations + '?' + queryParams,
      {},
    );
  }

  public async addUser(data: any): Promise<any> {
    if (this.useSdk && this.sdkWrapper) {
      return this.sdkWrapper.addUser(data);
    }
    return super.post(api.create_users, data);
  }

  public async editUser(user_id: string, data: any): Promise<any> {
    if (this.useSdk && this.sdkWrapper) {
      return this.sdkWrapper.editUser(user_id, data);
    }
    return super.put(api.update_user + user_id, data);
  }

  public async deleteUser(user_id: string): Promise<any> {
    if (this.useSdk && this.sdkWrapper) {
      return this.sdkWrapper.deleteUser(user_id);
    }
    return super.delete(api.delete_user + user_id, {});
  }

  public async checkUserOnline(): Promise<any> {
    return super.get(api.check_user_online);
  }

  public async getGroups(): Promise<any> {
    return super.get(api.get_groups);
  }

  public async getUserAcls(): Promise<any> {
    return this.dedupeGetRequest('getUserAcls', () =>
      super.get(api.get_doc_type_permissions),
    );
  }

  public async getUserOrganizations(): Promise<any> {
    return super.get(api.user_organisation);
  }

  public async getUserPreference(data: getPreferenceRequest): Promise<any> {
    const querystring = this.encodeQueryData(data);
    return super.get(api.get_user_preference + '?' + querystring);
  }

  public async setUserPreference(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.put(api.set_user_preference, formData);
  }

  public async getUserAllPreferences(): Promise<any> {
    return this.dedupeGetRequest('getUserAllPreferences', async () => {
      try {
        return await super.get(api.get_user_preferences);
      } catch (error: any) {
        // ✅ CRITICAL FIX: Don't trigger token refresh on preferences 401 errors
        // Preferences endpoint may return 401 for various reasons (service issues, org_id sync timing)
        // This should NOT cause app-wide reload or token refresh attempts
        if (error?.response?.status === 401) {
          logger.warn(
            '[PREFERENCES] GET /preferences/get_user_preferences returned 401. User preferences unavailable but app can continue with defaults.',
          );
          return {}; // Return empty preferences instead of propagating error
        }
        throw error; // Re-throw other errors
      }
    });
  }

  public async getInforAclInfo(): Promise<any> {
    return super.get(api.get_acl_settings);
  }

  public async createAclInitializer(data: FormData): Promise<any> {
    return super.post(api.create_acl_initializer, data);
  }

  public async createAclMapping(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.create_acl_mapping, formData);
  }

  public async deleteAclSetting(): Promise<any> {
    return super.delete(api.delete_acl_settings, {});
  }

  public async deleteAclMapping(map_id: string): Promise<any> {
    const formData = new FormData();
    formData.append('id', map_id);
    return super.delete(api.delete_acl_mapping, formData);
  }

  public async getTfidfPreference(key: string, tfidf_id: string): Promise<any> {
    const querystring = 'key=' + key + '&tfidf_id=' + tfidf_id;
    return super.get(api.get_tfidf_preference + '?' + querystring);
  }

  public async setTfidfPreference(formData: FormData): Promise<any> {
    return super.put(api.set_tfidf_preference, formData);
  }

  public async getDBCredentials(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.put(api.get_db_credentials, formData);
  }

  public async checkIfSystemAdminExists(): Promise<boolean> {
    try {
      const res = await this.getUsers();
      if (Array.isArray(res) && res.length > 0) {
        // Check if any user has is_system_user === true
        return res.some((user: any) => user.is_system_user === true);
      }
      return false;
    } catch (error) {
      // logError('Failed to check system admin:', error);
      console.error('Failed to check system admin:', error);
      return false;
    }
  }

  public async getUserLoginHistory(
    limit = 10,
    includeFailures = false,
  ): Promise<any> {
    const queryParams: string[] = [];
    if (limit) {
      queryParams.push(`limit=${limit}`);
    }
    if (includeFailures) {
      queryParams.push(`include_failures=${includeFailures}`);
    }
    const queryString =
      queryParams.length > 0 ? '?' + queryParams.join('&') : '';
    return super.get(api.get_user_login_history + queryString);
  }

  public async getUserLoginHistoryById(
    userId: string,
    limit = 10,
    includeFailures = false,
  ): Promise<any> {
    const queryParams: string[] = [];
    if (limit) {
      queryParams.push(`limit=${limit}`);
    }
    if (includeFailures) {
      queryParams.push(`include_failures=${includeFailures}`);
    }
    const queryString =
      queryParams.length > 0 ? '?' + queryParams.join('&') : '';
    const url =
      api.get_user_login_history_by_id.replace('{user_id}', userId) +
      queryString;
    return super.get(url);
  }

  // ============================================================
  // API Key Management Methods
  // ============================================================

  /**
   * List all API keys for the organization
   */
  public async getApiKeys(): Promise<ApiKeysListResponse> {
    return super.get(api.api_keys_list);
  }

  /**
   * Create a new API key
   * @param data - { name: string, description?: string, expires_at?: string }
   * @returns API key data including the full key (shown only once!)
   */
  public async createApiKey(data: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    return super.post(api.api_keys_create, data);
  }

  /**
   * Get a specific API key by ID
   * @param keyId - The API key UUID
   */
  public async getApiKey(keyId: string): Promise<ApiKeyResponse> {
    return super.get(api.api_keys_get + keyId);
  }

  /**
   * Update an API key's metadata
   * @param keyId - The API key UUID
   * @param data - { name?: string, description?: string }
   */
  public async updateApiKey(
    keyId: string,
    data: UpdateApiKeyRequest,
  ): Promise<ApiKeyResponse> {
    return super.patch(api.api_keys_update + keyId, data);
  }

  /**
   * Revoke an API key (cannot be undone)
   * @param keyId - The API key UUID
   */
  public async revokeApiKey(keyId: string): Promise<RevokeApiKeyResponse> {
    return super.delete(api.api_keys_revoke + keyId, {});
  }

  /**
   * Permanently delete a revoked API key
   * @param keyId - The API key UUID (must be revoked first)
   */
  public async deleteApiKey(keyId: string): Promise<void> {
    return super.delete(api.api_keys_revoke + keyId, {});
  }
}

// ============================================================
// API Key TypeScript Interfaces
// ============================================================

/**
 * API Key object returned from the backend
 */
export interface ApiKey {
  id: string;
  key_prefix: string;
  name: string | null;
  description: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  is_active: boolean;
  created_at: string;
  revoked_at: string | null;
}

/**
 * Response from GET /api/api-keys (list)
 */
export interface ApiKeysListResponse {
  keys: ApiKey[];
}

/**
 * Request body for POST /api/api-keys (create)
 */
export interface CreateApiKeyRequest {
  name: string;
  description?: string;
  expires_at?: string | null;
}

/**
 * Response from POST /api/api-keys (create)
 * Note: 'key' field contains the full API key and is only shown once!
 */
export interface CreateApiKeyResponse {
  id: string;
  key: string; // Full API key - shown only once!
  key_prefix: string;
  name: string;
  description: string | null;
  expires_at: string | null;
  created_at: string;
}

/**
 * Response from GET /api/api-keys/{id} (single key details)
 */
export interface ApiKeyResponse extends ApiKey {}

/**
 * Request body for PATCH /api/api-keys/{id} (update)
 */
export interface UpdateApiKeyRequest {
  name?: string;
  description?: string;
}

/**
 * Response from DELETE /api/api-keys/{id} (revoke)
 */
export interface RevokeApiKeyResponse {
  success: boolean;
  message?: string;
}
